/**
 * One set of input rules for every form, matching the API's own checks (the server always checks again).
 *
 *     const rules = useRules();
 *     // Zod forms
 *     const schema = z.object({ price: rules.money({ min: 1 }), stock: rules.whole({ max: 10_000_000 }) });
 *     // React Hook Form without Zod
 *     <NumberInput decimals={2} {...register("price", { validate: rules.check.money({ min: 1 }) })} />
 *
 * Pair each rule with the matching input, which stops wrong characters from being typed at all:
 * `NumberInput` (decimals 0 / 2) for numbers and `PatternInput` with `textRules.*` for slugs and codes.
 */

import { useMemo } from "react";
import { z } from "zod";

import { useTranslation } from "@motolynk/i18n";

/** Formats, the same as the backend's (vendors/validators.py, geo seeders, promotions, catalog, products). */
export const PATTERNS = {
  phone: /^(\+[1-9]\d{7,14}|0?\d{10})$/,
  pincode: /^[1-9]\d{5}$/,
  gstin: /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
  pan: /^[A-Z]{3}[ABCEFGHJLPT][A-Z]\d{4}[A-Z]$/,
  ifsc: /^[A-Z]{4}0[A-Z0-9]{6}$/,
  accountNumber: /^[A-Za-z0-9]{6,34}$/,
  couponCode: /^[A-Z0-9][A-Z0-9-]{2,39}$/,
  attributeCode: /^[a-z][a-z0-9_]{1,59}$/,
  sku: /^[A-Za-z0-9][A-Za-z0-9._/-]{0,63}$/,
  hsn: /^(\d{4}|\d{6}|\d{8})$/,
  registration: /^[A-Z0-9]{4,15}$/,
} as const;

export type PatternName = keyof typeof PATTERNS;

/** Phone numbers are checked without the spaces, dashes and brackets people type. */
export function compactPhone(value: string): string {
  return value.replace(/[\s\-()]/g, "");
}

export interface NumberRule {
  /** Digits allowed after the point (0 = whole numbers). */
  decimals?: number;
  min?: number;
  max?: number;
  /** Empty is an error (default: empty is allowed and means "not set"). */
  required?: boolean;
}

/** What is wrong with a value, as a translation key plus its values (null = fine). */
export type Problem = { key: string; values?: Record<string, unknown> } | null;

export function numberProblem(value: unknown, rule: NumberRule = {}): Problem {
  const text = value === null || value === undefined ? "" : String(value).trim();
  if (!text) return rule.required ? { key: "required" } : null;
  const decimals = rule.decimals ?? 0;
  const shape = decimals > 0 ? new RegExp(`^-?\\d+(\\.\\d{1,${decimals}})?$`) : /^-?\d+$/;
  if (!shape.test(text)) {
    return decimals > 0 ? { key: "decimals", values: { count: decimals } } : { key: "wholeNumber" };
  }
  const number = Number(text);
  if (rule.min !== undefined && number < rule.min) return { key: "min", values: { min: rule.min } };
  if (rule.max !== undefined && number > rule.max) return { key: "max", values: { max: rule.max } };
  return null;
}

export function patternProblem(value: unknown, name: PatternName, required = false): Problem {
  let text = value === null || value === undefined ? "" : String(value).trim();
  if (!text) return required ? { key: "required" } : null;
  if (name === "phone") text = compactPhone(text);
  return PATTERNS[name].test(text) ? null : { key: name };
}

/** Largest money amount the API stores (Decimal 14,2 is capped lower by the services). */
export const MAX_MONEY = 99_999_999.99;

/**
 * `namespace` holds the `validation.*` messages: "common" in the panels, "storefront" in the storefront (which
 * imports this file as `@motolynk/app-core/validation`).
 */
export function useRules(namespace = "common") {
  const { t } = useTranslation(namespace);
  return useMemo(() => {
    const say = (problem: Problem) =>
      problem ? t(`validation.${problem.key}`, problem.values ?? {}) : undefined;

    const numberRule = (rule: NumberRule) =>
      z
        .string()
        .trim()
        .superRefine((value, ctx) => {
          const message = say(numberProblem(value, rule));
          if (message) ctx.addIssue({ code: "custom", message });
        });
    const patternRule = (name: PatternName, required = false) =>
      z
        .string()
        .trim()
        .superRefine((value, ctx) => {
          const message = say(patternProblem(value, name, required));
          if (message) ctx.addIssue({ code: "custom", message });
        });

    const presets = {
      /** Rupees and paise: 2 decimals, 0 to MAX_MONEY unless narrowed. */
      money: (rule: Omit<NumberRule, "decimals"> = {}): NumberRule => ({
        min: 0,
        max: MAX_MONEY,
        ...rule,
        decimals: 2,
      }),
      /** A percentage with up to 2 decimals, 0 to 100 unless narrowed (e.g. TDS max 10). */
      percent: (rule: Omit<NumberRule, "decimals"> = {}): NumberRule => ({
        min: 0,
        max: 100,
        ...rule,
        decimals: 2,
      }),
      /** Counts, grams, millimetres, days, sort order: digits only, from 0 unless narrowed. */
      whole: (rule: Omit<NumberRule, "decimals"> = {}): NumberRule => ({
        min: 0,
        ...rule,
        decimals: 0,
      }),
    };

    /** React Hook Form `validate` functions: return the message, or true when fine. */
    const check = {
      number: (rule: NumberRule) => (value: unknown) => say(numberProblem(value, rule)) ?? true,
      money: (rule?: Omit<NumberRule, "decimals">) => (value: unknown) =>
        say(numberProblem(value, presets.money(rule))) ?? true,
      percent: (rule?: Omit<NumberRule, "decimals">) => (value: unknown) =>
        say(numberProblem(value, presets.percent(rule))) ?? true,
      whole: (rule?: Omit<NumberRule, "decimals">) => (value: unknown) =>
        say(numberProblem(value, presets.whole(rule))) ?? true,
      pattern:
        (name: PatternName, required = false) =>
        (value: unknown) =>
          say(patternProblem(value, name, required)) ?? true,
    };

    /** For forms that keep a whole number as a number (`register(name, { valueAsNumber: true })`). */
    const count = ({ min = 0, max }: { min?: number; max?: number } = {}) => {
      const rule = z
        .number({ error: say({ key: "wholeNumber" }) })
        .int(say({ key: "wholeNumber" }))
        .min(min, say({ key: "min", values: { min } }));
      return max === undefined ? rule : rule.max(max, say({ key: "max", values: { max } }));
    };

    return {
      number: numberRule,
      count,
      money: (rule?: Omit<NumberRule, "decimals">) => numberRule(presets.money(rule)),
      percent: (rule?: Omit<NumberRule, "decimals">) => numberRule(presets.percent(rule)),
      whole: (rule?: Omit<NumberRule, "decimals">) => numberRule(presets.whole(rule)),
      pattern: patternRule,
      check,
      /** The message for a problem (for hand-written checks). */
      message: say,
    };
  }, [t]);
}

export type Rules = ReturnType<typeof useRules>;
